import { PageMeta } from '@psl/appComponents/seo/PageMeta';
import {
  kDefaultPageDescription,
  kDefaultPageTitle,
} from '@psl/appComponents/seo/PageMeta.web';
import {
  BottomTabNavigationOptions,
  BottomTabNavigationProp,
} from '@react-navigation/bottom-tabs';
import { useRoute } from '@react-navigation/core';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationOptions } from '@react-navigation/stack';
import { ComponentProps, FC, memo, ReactNode, Suspense, useMemo } from 'react';
import { PageLoading } from '../../../appComponents/PageLoading';
import { RxAutoHangUpProviderForPage } from '../reactNavigationHelpers';
import {
  NavigatorScreenExtraProps,
  WrappedNavigatorScreen,
} from './wrapNavigatorScreen';

export type WrappedBottomTabNavigator<T extends object> = FC<void> & {
  component: FC<T>;
  options: BottomTabNavigationOptions;
  extraProps?: any;
};

export interface WrapNavigatorScreenOptions extends StackNavigationOptions {
  suspenseFallback?: ReactNode;
}

export function wrapBottomTabNavigatorScreen<T extends object>(
  Component: FC<T>,
  options?:
    | WrapNavigatorScreenOptions
    | ((props: {
        route: { params: T };
        navigation: BottomTabNavigationProp<any>;
      }) => WrapNavigatorScreenOptions),
  extraProps?: NavigatorScreenExtraProps,
): WrappedBottomTabNavigator<T> {
  options = options ?? {};
  const { title, description, ...extraScreenProps } = extraProps ?? {};

  const NavigatorTabWrapper = memo(function () {
    const route = useRoute() as unknown as { params: T };
    const navigation = useNavigation<any>();
    const navOptions = useMemo(
      () =>
        typeof options === 'function'
          ? options({ route, navigation })
          : options,
      [navigation, route],
    );
    return (
      <RxAutoHangUpProviderForPage>
        <PageMeta
          title={title ?? kDefaultPageTitle}
          description={description ?? kDefaultPageDescription}
        />
        <Suspense fallback={navOptions?.suspenseFallback ?? null}>
          <Component {...route.params} />
        </Suspense>
      </RxAutoHangUpProviderForPage>
    );
  }) as any;

  NavigatorTabWrapper.component = Component;
  NavigatorTabWrapper.options = options as StackNavigationOptions;
  NavigatorTabWrapper.extraProps = extraScreenProps;

  return NavigatorTabWrapper;
}

export function wrapLazyBottomTabNavigatorScreen<T extends object>(
  Component: FC<T>,
  options?:
    | WrapNavigatorScreenOptions
    | ((props: {
        route: { params: T };
        navigation: BottomTabNavigationProp<any>;
      }) => WrapNavigatorScreenOptions),
  extraProps?: NavigatorScreenExtraProps,
): WrappedBottomTabNavigator<T> {
  const wrappedOptions =
    typeof options === 'function'
      ? (props: {
          route: { params: T };
          navigation: BottomTabNavigationProp<any>;
        }) => {
          const ret = options(props);
          return {
            suspenseFallback: <PageLoading />,
            ...ret,
          };
        }
      : {
          suspenseFallback: <PageLoading />,
          ...options,
        };
  return wrapBottomTabNavigatorScreen(Component, wrappedOptions, extraProps);
}

export type TabToParams<
  Screens extends { [k: string]: WrappedBottomTabNavigator<any> },
> = {
  [P in keyof Screens]: Omit<
    ComponentProps<Screens[P]['component']>,
    'children'
  >;
};

export function spreadBottomTabs(
  StackScreen: any,
  screens: {
    [k: string]: WrappedBottomTabNavigator<any> | WrappedNavigatorScreen<any>;
  },
  initialScreen?: string,
  initialParams?: any,
) {
  return Object.entries(screens).map(([name, component]) => (
    <StackScreen
      key={name}
      name={name as any}
      component={component}
      options={component.options}
      initialParams={name === initialScreen ? initialParams : undefined}
      {...component.extraProps}
    />
  ));
}
